import { captureException } from "@sentry/browser";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { FetchError } from "common/types/fetchError";
import { handleFetchError } from "lib/fetcher";

const DEFAULT_STALE_TIME = 90_000;

// 401, 404, 409 are expected business logic errors — skip Sentry noise.
// Everything else (500, network failure, parse errors) is unexpected and worth tracking.
// Previously SWR silent swallowing most HTTP errors, so we're keeping this list consistent.
// Will circle back to see if sentry gets flooded and if we need to adjust this list.
const EXPECTED_ERROR_STATUSES = [401, 404, 409];

// Query errors are always FetchError (thrown by authGet).
// Fires after retries are exhausted (retry: 1) so no duplicate Sentry reports.
function handleQueryCacheError(error: unknown, queryKey: unknown) {
  if (error instanceof FetchError) {
    // 401 redirects to login; other expected statuses are silently ignored
    handleFetchError(error, window.location.pathname);
    if (!EXPECTED_ERROR_STATUSES.includes(error.status ?? Infinity)) {
      console.error("Query error:", error, { queryKey });
      captureException(error, { extra: { queryKey } });
    }
  } else {
    // Unexpected non-FetchError (e.g. network failure, JSON parse error)
    console.error("Unexpected query error:", error, { queryKey });
    captureException(error, { extra: { queryKey } });
  }
}

// UserNotificationType is already shown to the user via onError: setUserNotification — skip Sentry.
// Anything else is unexpected and captured.
function handleMutationCacheError(error: unknown, mutationKey: unknown) {
  if (!isUserNotification(error)) {
    captureException(error, { extra: { mutationKey } });
  }
}

function isUserNotification(error: unknown): boolean {
  return typeof error === "object" && error !== null && "messageType" in error && "message" in error;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => handleQueryCacheError(error, query.queryKey),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => handleMutationCacheError(error, mutation.options.mutationKey),
  }),
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
